import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import AttachmentItemsSortOrder from 'owa-service/lib/contract/AttachmentItemsSortOrder';
import callFileProviderAPI from '../../utils/callFileProviderAPI';
import FileProviderServiceResponse from '../../types/FileProviderServiceResponse';
import OneDriveItem from '../../types/OneDriveItem';
import { API_BASE_FOR_ID, ROOT_FOLDER_ID } from './constants';
import { assertNever } from 'owa-assert';
import { COLUMN_NAMES, getAllColumnNames } from './columns';
import { parse as parseUrl } from 'url';

function getSortColumnName(sortColumn: AttachmentItemsSortColumn): string {
    switch (sortColumn) {
        case AttachmentItemsSortColumn.Name:
            return COLUMN_NAMES.name;
        case AttachmentItemsSortColumn.Size:
            return COLUMN_NAMES.size;
        default:
            return COLUMN_NAMES.lastModifiedDateTime;
    }
}

function getSortOrderName(sortOrder: AttachmentItemsSortOrder): 'asc' | 'desc' {
    switch (sortOrder) {
        case AttachmentItemsSortOrder.Ascending:
            return 'asc';
        case AttachmentItemsSortOrder.Descending:
            return 'desc';
        default:
            return assertNever(sortOrder);
    }
}

function getFilterQuery(filterType: OneDriveItemFilterType): string {
    switch (filterType) {
        case OneDriveItemFilterType.None:
            return '';
        case OneDriveItemFilterType.Folder:
            return `(${COLUMN_NAMES.folder} ne null)`;
        default:
            return assertNever(filterType);
    }
}

interface GetOneDriveItemsResponse {
    value: OneDriveItem[];
    '@odata.nextLink'?: string;
}

export enum OneDriveItemFilterType {
    None,
    Folder,
}

export interface GetOneDriveItemsPagingInfo {
    maxEntries: number;
    skipToken?: string;
}

export interface GetOneDriveItemsResult {
    items: OneDriveItem[];
    skipToken?: string;
}

export default async function getOneDriveItems(
    apiBaseUrl: 'https://s2s-api.onedrive.com',
    providerType: AttachmentDataProviderType.OneDriveConsumer,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    filter?: OneDriveItemFilterType,
    pagingInfo?: GetOneDriveItemsPagingInfo
): Promise<FileProviderServiceResponse<GetOneDriveItemsResult>>;
export default async function getOneDriveItems(
    apiBaseUrl: string,
    providerType: AttachmentDataProviderType.OneDrivePro,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    filter?: OneDriveItemFilterType,
    pagingInfo?: GetOneDriveItemsPagingInfo
): Promise<FileProviderServiceResponse<GetOneDriveItemsResult>>;
export default async function getOneDriveItems(
    apiBaseUrl: string,
    providerType:
        | AttachmentDataProviderType.OneDriveConsumer
        | AttachmentDataProviderType.OneDrivePro,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    filter: OneDriveItemFilterType = OneDriveItemFilterType.None,
    pagingInfo?: GetOneDriveItemsPagingInfo
): Promise<FileProviderServiceResponse<GetOneDriveItemsResult>> {
    folderId = folderId || ROOT_FOLDER_ID;

    const sortColumnName = getSortColumnName(sortColumn);
    const sortOrderName = getSortOrderName(sortOrder);

    let query = `expand=thumbnails(select=medium)&orderby=folder,${sortColumnName} ${sortOrderName}&select=${getAllColumnNames()}`;

    if (filter !== OneDriveItemFilterType.None) {
        query = `${query}&filter=${getFilterQuery(filter)}`;
    }

    if (pagingInfo) {
        // If paging info is provided then add the max entries required
        query = `${query}&$top=${pagingInfo.maxEntries}`;

        // If there is a skip token for next page then add that as well
        if (pagingInfo.skipToken) {
            query = `${query}&$skiptoken=${pagingInfo.skipToken}`;
        }
    }

    const url = `${apiBaseUrl}/${API_BASE_FOR_ID}/${folderId}/children?${query}`;

    const response = await callFileProviderAPI(
        url,
        providerType,
        // This header is needed for now while the OneDrive teams fixes an issue with sorting
        // when there are 5000+ items in a folder. The same issue exists with the CSOM APIs we
        // use in OWS right now, so this won't be a regression.
        {
            requestInit: {
                headers: new Headers({
                    Prefer: 'HonorNonIndexedQueriesWarningMayFailRandomly',
                }),
            },
        }
    );

    if (response.ok) {
        const responseBody = await response.text();
        const getItemsResponse = JSON.parse(responseBody) as GetOneDriveItemsResponse;
        const skipToken = getItemsResponse['@odata.nextLink']
            ? getSkipToken(decodeURI(getItemsResponse['@odata.nextLink']))
            : null;

        return { errorResponse: null, result: { items: getItemsResponse.value, skipToken } };
    } else {
        return { errorResponse: response, result: null };
    }
}

function getSkipToken(url: string): string {
    const parsedUrl = parseUrl(url, true /* parseQueryString */);
    return parsedUrl.query ? parsedUrl.query.$skiptoken : null;
}
